import { Component, OnInit } from '@angular/core';
import { CoreUIToastV2Service } from './toast.service';
import { CoreUIToastConfig, CoreUIToastDirection } from './toast-config';
import { trigger, transition, style, animate } from '@angular/animations';

export type CoreUIToastActionRole = 'primary' | 'secondary' | 'danger';

type ToastVisualType = 'success' | 'error' | 'warning' | 'info' | 'update' | 'delete';

export interface CoreUIToastAction {
    label: string;
    role?: CoreUIToastActionRole;
    iconName?: string;
    onClick?: () => void | Promise<void>;
    loading?: boolean;
    disabled?: boolean;
}

export interface CoreUIToastData {
    type?: 'success' | 'error' | 'warning' | 'info' | 'delete' | 'update';
    title?: string;
    msg?: string;
    msgHtml?: string;
    direction?: CoreUIToastDirection;
    size?: 'small' | 'medium' | 'large';
    iconName?: string;
    iconColor?: string;
    loading?: boolean;

    autoClose?: boolean;
    autoCloseTime?: number;

    actions?: CoreUIToastAction[];
    closable?: boolean;
    onClose?: () => void;
}

@Component({
    selector: 'core-ui-toast',
    templateUrl: './core-ui-toast.component.html',
    styleUrls: ['./core-ui-toast.component.scss'],
    standalone: false,
    animations: [
        trigger('fadeInOut', [
            transition(':enter', [style({ opacity: 0 }), animate('300ms', style({ opacity: 1 }))]),
            transition(':leave', [animate('300ms', style({ opacity: 0 }))]),
        ]),
    ],
})
export class CoreUIToastComponent implements OnInit {

    config: CoreUIToastConfig | null = null;
    direction = CoreUIToastDirection.topCenter;

    title = '';
    message = '';
    messageHtml = '';
    iconName = 'icon_info';
    iconColor = '#0375FC';
    size = 'medium';

    toastType: ToastVisualType = 'info';
    actions: CoreUIToastAction[] = [];
    closable = true;
    loading = false;
    isLoadingControlled = false;
    private autoCloseTimeoutId: ReturnType<typeof setTimeout> | null = null;

    constructor(private toastService: CoreUIToastV2Service) { }

    ngOnInit() {
        this.toastService.toasts$.subscribe((newConfig: CoreUIToastConfig | null) => {
            this.clearAutoCloseTimeout();

            if (!newConfig) {
                this.resetToast();
                return;
            }

            this.config = newConfig;

            this.direction = newConfig.data?.direction || CoreUIToastDirection.topCenter;
            this.title = newConfig.data?.title || 'Notificación';
            this.message = newConfig.data?.msg || '';
            this.messageHtml = newConfig.data?.msgHtml || '';
            this.size = newConfig.data?.size || 'medium';
            this.isLoadingControlled = Object.prototype.hasOwnProperty.call(newConfig.data ?? {}, 'loading');
            this.loading = newConfig.data?.loading === true;
            this.actions = newConfig.data?.actions ?? [];
            this.closable = newConfig.data?.closable !== false;

            const type = newConfig.data?.type || 'info';

            switch (type) {
                case 'success':
                    this.toastType = 'success';
                    this.iconName = newConfig.data?.iconName || 'icon_check-circle';
                    this.iconColor = newConfig.data?.iconColor || '#039855';
                    break;
                case 'error':
                    this.toastType = 'error';
                    this.iconName = newConfig.data?.iconName || 'icon_close-red';
                    this.iconColor = newConfig.data?.iconColor || '#C1492B';
                    break;
                case 'delete':
                    this.toastType = 'delete';
                    this.iconName = newConfig.data?.iconName || 'icon_detele';
                    this.iconColor = newConfig.data?.iconColor || '#D92D20';
                    break;
                case 'update':
                    this.toastType = 'update';
                    this.iconName = newConfig.data?.iconName || 'icon_save';
                    this.iconColor = newConfig.data?.iconColor || '#F59E0B';
                    break;
                case 'warning':
                    this.toastType = 'warning';
                    this.iconName = newConfig.data?.iconName || 'icon_warning';
                    this.iconColor = newConfig.data?.iconColor || '#F59E0B';
                    break;
                default:
                    this.toastType = 'info';
                    this.iconName = newConfig.data?.iconName || 'icon_info';
                    this.iconColor = newConfig.data?.iconColor || '#0375FC';
            }

            if (newConfig.data?.autoClose && this.actions.length === 0) {
                this.autoCloseTimeoutId = setTimeout(() => {
                    this.cerrar();
                    this.autoCloseTimeoutId = null;
                }, newConfig.data?.autoCloseTime || 5000);
            }
        });
    }

    get hasLoadingAction(): boolean {
        return this.loading || this.actions.some(action => action.loading);
    }

    isActionLoading(action: CoreUIToastAction): boolean {
        return action.loading === true || (this.loading && action.role === 'primary');
    }

    async onActionClick(action: CoreUIToastAction): Promise<void> {
        if (action.disabled || this.isActionLoading(action) || this.hasLoadingAction) return;

        const shouldClose = !(this.isLoadingControlled && action.role === 'primary');

        if (shouldClose) this.cerrar();

        queueMicrotask(() => action.onClick?.());
    }

    cerrar() {
        this.config?.data?.onClose?.();
        this.clearAutoCloseTimeout();
        this.resetToast();
    }

    private resetToast(): void {
        this.config = null;
        this.actions = [];
        this.messageHtml = '';
        this.loading = false;
        this.isLoadingControlled = false;
    }

    private clearAutoCloseTimeout(): void {
        if (!this.autoCloseTimeoutId) return;
        clearTimeout(this.autoCloseTimeoutId);
        this.autoCloseTimeoutId = null;
    }
}
